import equal from 'fast-deep-equal/react';
import {
    Contains,
    Divider, Interpolate, Message, Tooltip
} from '@wg/wows-react-uikit';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import styles from './RewardTooltip.scss';
import { MainShipDisclaimer } from '~/Components/Tooltips/Tooltips';
import TooltipGameReward from '~/Components/TooltipGameReward/TooltipGameReward';
import { GRADES } from '~/constants';
import * as Api from '~/types';
import { settings } from '~/Components/DownloadGamePopup/settings';
import QuantityText from '~/Components/QuantityText/QuantityText';
import { IContainsItem } from '@wg/wows-react-uikit/components/commerce/Contains/Contains';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';

interface IRewardTooltipProps {
    reward: Api.IReward,
    isCollected: boolean,
    shipLevelDescription?: string,
    isPremium?: boolean,
    isFreeMainReward?: boolean,
    secondaryShip?: Api.GameItemReward | null,
}

const LimitForDisplay = 3;

const RewardTooltip = (props: IRewardTooltipProps) => {
    const { t } = useTranslation();
    const {
        reward, isCollected, shipLevelDescription, isPremium, isFreeMainReward, secondaryShip
    } = props;
    const renderWithAmount = reward.gameRewards.length === 1;

    const renderReward = () => {
        return (
            <div className={styles.rewardRow}>
                <div className={styles.rewardHolder}>
                    <div className={styles.rewardItem}>
                        <TooltipGameReward
                            imgSrc={reward.isMainReward && settings.isPublicTestMode ? reward.extraImage : reward.image}
                            {...reward.gameRewards[0]}
                            hideTooltip
                            withAmount={!renderWithAmount}
                        />
                    </div>
                </div>
            </div>
        )
    }

    const renderContainsWidget = () => {
        const items: IContainsItem[] = reward.gameRewards.map((reward) => {
            const additionalProps = {};
            if (reward.amount) {
                additionalProps['amount'] = reward.amount;
            }

            return {
                content: (
                    <div className={styles.rewardWeItem}>
                        <WoWSEntity 
                            type={reward.type as string} 
                            id={reward.id} 
                            customisation={reward.customisation}
                            presentation={{
                                withText: true,
                                withTooltip: true,
                                iconPosition: 'left',
                                renderWithoutFade: true,
                            }}
                            {...additionalProps}                       
                        />
                    </div>
                )
            }
        });

        return (
            <div className={styles.rewardContainWidget}>
                <Contains
                    items={items} 
                    limit={items.length - LimitForDisplay <= 1 ? items.length : LimitForDisplay}
                    classNames={{
                        title: styles.rewardContainWidgetTitle,
                    }}
                />
            </div>
        )
    }

    const renderTitle = () => {
        switch (reward.grade) {
            case GRADES.BRONZE:
            case GRADES.SILVER: {
                return <Interpolate str={t('Особая награда за %(level)s этап')} level={reward.shipLevelNumber} />;
            }
            case GRADES.GOLD: {
                return t('Финальная награда');
            }
            default: {
                return <Interpolate str={t('Награда за %(level)s этап')} level={reward.shipLevelNumber} />;
            }
        }
    };

    const header = classnames(styles.header, {
        [styles.receivedReward]: isCollected,
    });

    return (
        <Tooltip>
            <div className={styles.wraper}>
                <div className={header}>{renderTitle()}</div>
                <Divider type={'major'} />
                {reward.gameRewards.length > 1 ? renderContainsWidget() : renderReward()}
                {isCollected && (
                    <>
                        <Divider type={'major'} />
                        <div className={styles.row}>
                            <Message style={'inline'} type={'done'}>
                                {t('Получено')}
                            </Message>
                        </div>
                    </>
                )}
                {shipLevelDescription && (
                    <>
                        <Divider type={'major'} />
                        {renderWithAmount && (
                            <>
                                <QuantityText
                                    type={reward.gameRewards[0].type as string}
                                    amount={reward.gameRewards[0].amount as number}
                                />
                                <Divider type={'major'} />
                            </>
                        )}
                        <div className={styles.descriptionRow}>{shipLevelDescription}</div>
                    </>
                )}
                <MainShipDisclaimer
                    isPremium={isPremium}
                    isFreeMainReward={isFreeMainReward}
                    secondaryShip={secondaryShip}
                />
            </div>
        </Tooltip>
    );
};

export default memo(RewardTooltip, equal);
